import { useCallback } from 'react';
import { useContext, useContextSelector } from 'use-context-selector';
import { InternationalizationContextActionTypes, InternationalizationDispatchOutput } from './types';
import { InternationalizationContext, InternationalizationDispatchContext } from './InternationalizationContext';

const useInternationalizationState = () => {
  const context = useContext(InternationalizationContext);

  if (context === undefined) {
    throw new Error('useInternationalizationState must be used within InternationalizationProvider');
  }

  return context;
};

const useLanguage = () => {
  const language = useContextSelector(InternationalizationContext, state => state.language);

  if (language === undefined) {
    throw new Error('useLanguage must be used within InternationalizationProvider');
  }

  return language;
};

const useAvailableLanguages = () => {
  const availableLanguages = useContextSelector(InternationalizationContext, state => state.availableLanguages);

  if (availableLanguages === undefined) {
    throw new Error('useAvailableLanguages must be used within InternationalizationProvider');
  }

  return availableLanguages;
};

const useInternationalizationDispatch = (): InternationalizationDispatchOutput => {
  const dispatch = useContext(InternationalizationDispatchContext);

  if (dispatch === undefined) {
    throw new Error('useInternationalizationDispatch must be used within a InternationalizationProvider');
  }

  const setAvailableLanguages = useCallback(
    payload => dispatch({ type: InternationalizationContextActionTypes.SetAvailableLanguages, payload }),
    [dispatch],
  );

  const setLanguage = useCallback(
    payload => dispatch({ type: InternationalizationContextActionTypes.SetLanguage, payload }),
    [dispatch],
  );

  return {
    setAvailableLanguages,
    setLanguage,
  };
};

export { useInternationalizationState, useInternationalizationDispatch, useLanguage, useAvailableLanguages };
